import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { dict } from "@/i18n";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const orig = err.config;
    if (err.response?.status === 401 && orig && !orig._retry && !String(orig.url).includes("/auth/")) {
      orig._retry = true;
      try {
        await api.post("/auth/refresh");
        return api(orig);
      } catch {}
    }
    return Promise.reject(err);
  }
);

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export const lineKey = (productId, options) =>
  productId + "|" + (options ? `k:${options.knee}|b:${options.bag}` : "plain");

export function StoreProvider({ children }) {
  const [lang, setLang] = useState("en");
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pamak-cart-v2")) || []; } catch { return []; }
  });
  const [products, setProducts] = useState([]);
  const [config, setConfig] = useState({});
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const toastTimer = useRef(null);

  const t = useCallback((k, vars) => {
    let s = dict[lang]?.[k] ?? dict.en[k] ?? k;
    if (vars) Object.entries(vars).forEach(([key, v]) => { s = s.split("{" + key + "}").join(String(v)); });
    return s;
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("pamak-cart-v2", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
    api.get("/config").then((r) => setConfig(r.data)).catch(() => setConfig({}));
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setAuthLoading(false));
  }, []);

  const toast = useCallback((msg) => {
    setToastMsg(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(""), 2400);
  }, []);

  const displayName = useCallback((product) => {
    if (product?.group === "knee") return t("product.knee");
    if (product?.group === "bag") return t("product.bag");
    if (product?.group === "set") return t("product.set");
    return product?.name ?? "";
  }, [t]);

  const addToCart = useCallback((product, options) => {
    const key = lineKey(product.id, options || null);
    setCart((prev) => {
      const found = prev.find((i) => i.key === key);
      if (found) return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + 1 } : i));
      return [...prev, {
        key,
        productId: product.id,
        name: product.name,
        color: product.color,
        price: product.price,
        qty: 1,
        image: product.image,
        group: product.group,
        options: options ? { ...options } : null,
        addons: [],
      }];
    });
    toast(`${displayName(product)} ${t("toast.added")}`);
  }, [toast, t, displayName]);

  const changeQty = useCallback((key, delta) => {
    setCart((prev) => prev
      .map((i) => (i.key === key ? { ...i, qty: i.qty + delta } : i))
      .filter((i) => i.qty > 0));
  }, []);

  const removeLine = useCallback((key) => {
    setCart((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const mergeAddons = (a, b) => {
    const out = (a || []).map((x) => ({ ...x }));
    (b || []).forEach((y) => {
      const f = out.find((x) => x.id === y.id);
      if (f) f.qty += y.qty;
      else out.push({ ...y });
    });
    return out;
  };

  const changeColor = useCallback((key, sibling) => {
    setCart((prev) => {
      const line = prev.find((i) => i.key === key);
      if (!line) return prev;
      const nextKey = lineKey(sibling.id, null);
      const updated = { ...line, key: nextKey, productId: sibling.id, color: sibling.color, image: sibling.image };
      const clash = prev.find((i) => i.key === nextKey && i.key !== key);
      if (!clash) return prev.map((i) => (i.key === key ? updated : i));
      return prev
        .filter((i) => i.key !== key)
        .map((i) => (i.key === nextKey ? { ...i, qty: i.qty + updated.qty, addons: mergeAddons(i.addons, updated.addons) } : i));
    });
  }, []);

  const setOption = useCallback((key, which, value) => {
    setCart((prev) => {
      const line = prev.find((i) => i.key === key);
      if (!line) return prev;
      const options = { ...line.options, [which]: value };
      const nextKey = lineKey(line.productId, options);
      const updated = { ...line, options, key: nextKey };
      const clash = prev.find((i) => i.key === nextKey && i.key !== key);
      if (!clash) return prev.map((i) => (i.key === key ? updated : i));
      return prev
        .filter((i) => i.key !== key)
        .map((i) => (i.key === nextKey ? { ...i, qty: i.qty + updated.qty, addons: mergeAddons(i.addons, updated.addons) } : i));
    });
  }, []);

  const addAddon = useCallback((key, addon) => {
    setCart((prev) => prev.map((i) => {
      if (i.key !== key) return i;
      const found = i.addons.find((a) => a.id === addon.id);
      const addons = found
        ? i.addons.map((a) => (a.id === addon.id ? { ...a, qty: a.qty + 1 } : a))
        : [...i.addons, { id: addon.id, name: addon.name, price: addon.price, image: addon.image, qty: 1 }];
      return { ...i, addons };
    }));
  }, []);

  const changeAddonQty = useCallback((key, addonId, delta) => {
    setCart((prev) => prev.map((i) => {
      if (i.key !== key) return i;
      const addons = i.addons
        .map((a) => (a.id === addonId ? { ...a, qty: a.qty + delta } : a))
        .filter((a) => a.qty > 0);
      return { ...i, addons };
    }));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const lineTotal = useCallback((line) =>
    line.price * line.qty + (line.addons || []).reduce((s, a) => s + a.price * a.qty, 0), []);

  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.qty, 0), [cart]);
  const cartTotal = useMemo(() => cart.reduce((s, i) => s + lineTotal(i), 0), [cart, lineTotal]);
  const money = useCallback((v) => "$" + Number(v).toFixed(2), []);

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next = prev === "en" ? "kh" : "en";
      document.body.classList.toggle("khmer", next === "kh");
      document.documentElement.lang = next === "kh" ? "km" : "en";
      return next;
    });
  }, []);

  const scrollToSection = useCallback((id) => {
    setMenuOpen(false);
    setCartOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.3 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const closeAll = useCallback(() => {
    setCartOpen(false);
    setCheckoutOpen(false);
    setMenuOpen(false);
  }, []);

  const openAuth = useCallback((mode) => {
    setAuthMode(mode || "login");
    setAuthOpen(true);
  }, []);

  const openFaq = useCallback(() => setFaqOpen(true), []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    toast(t("toast.bye"));
  }, [toast, t]);

  const value = {
    API, api, lang, t, toggleLang, config,
    cart, products, addToCart, changeQty, removeLine, changeColor, setOption,
    addAddon, changeAddonQty, lineTotal, clearCart,
    cartCount, cartTotal, money, displayName,
    cartOpen, setCartOpen, checkoutOpen, setCheckoutOpen,
    menuOpen, setMenuOpen, closeAll,
    toastMsg, toast, scrollToSection,
    user, setUser, authLoading, authOpen, setAuthOpen, authMode, setAuthMode, openAuth, logout,
    faqOpen, setFaqOpen, openFaq,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}
